import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  ApplicationCreateRequestBody,
  ApplicationDetail,
  ApplicationQuestionMapping,
  ApplicationsService,
  ApplicationUpdateRequestBody
} from '../../services/applications.service';
import { RunService } from '../../services/service';
import { Questions } from '../../models/questions';

/** Fields shown on create/edit; aligned with API names the user specified. */
export interface ApplicationFormModel {
  applicationName: string;
  IsActive: boolean;
  ContactName: string;
  ContactEmail: string;
  WebhookUrl: string;
  WebhookAuthenticationKey: string;
  WebhookAuthenticationType: string;
}

@Component({
  selector: 'app-application-details',
  templateUrl: './application-details.component.html',
  styleUrls: ['./application-details.component.scss']
})
export class ApplicationDetailsComponent implements OnInit, OnDestroy {
  /** Present when URL is /application/:id */
  id: string | null = null;
  /** Numeric id from GET detail; required for PUT body. */
  applicationId: number | null = null;
  isCreateMode = true;
  formModel: ApplicationFormModel = ApplicationDetailsComponent.emptyForm();
  /** GET detail only; shown read-only in edit mode. */
  readonlyClientId = '';
  readonlyAuthenticationKey = '';
  loading = false;
  error: string | null = null;
  saving = false;
  saveError: string | null = null;
  saveSuccess: string | null = null;
  statusSaving = false;
  questionMappings: ApplicationQuestionMapping[] = [];
  mappingPopoverOpen = false;
  mappingModalMode: 'add' | 'edit' = 'add';
  editingMappingId: number | null = null;
  mappingDeletingId: number | null = null;
  mappingActionError: string | null = null;
  confirmatoryQuestions: Questions[] = [];
  filteredConfirmatoryQuestions: Questions[] = [];
  questionFilterText = '';
  selectedQuestionId: number | null = null;
  integrationQuestionId = '';
  loadingQuestions = false;
  mappingPopoverError: string | null = null;
  mappingSaving = false;
  loadingQuestionCategories = false;
  questionHasCategories = false;
  justSendCategories = false;
  private confirmatoryQuestionsLoaded = false;
  readonly webhookAuthTypes = ['None', 'Bearer Token', 'API Key'] as const;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private applicationsService: ApplicationsService,
    private runService: RunService
  ) { }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),
        map((pm) => pm.get('id'))
      )
      .subscribe((id) => {
        this.id = id;
        this.isCreateMode = !id;
        this.applicationId = null;
        this.readonlyClientId = '';
        this.readonlyAuthenticationKey = '';
        this.questionMappings = [];
        this.closeMappingPopover();
        this.error = null;
        this.saveError = null;
        this.saveSuccess = null;
        this.formModel = ApplicationDetailsComponent.emptyForm();
        if (id) {
          this.loadApplication(id);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private static normalizeAuthType(value: string): string {
    const trimmed = value.trim();
    return trimmed === '' ? 'None' : trimmed;
  }

  private static emptyForm(): ApplicationFormModel {
    return {
      applicationName: '',
      IsActive: false,
      ContactName: '',
      ContactEmail: '',
      WebhookUrl: '',
      WebhookAuthenticationKey: '',
      WebhookAuthenticationType: 'None'
    };
  }

  private static pickString(raw: ApplicationDetail, ...keys: string[]): string {
    for (const key of keys) {
      const v = raw[key];
      if (v === undefined || v === null) {
        continue;
      }
      return String(v);
    }
    return '';
  }

  private static pickBool(raw: ApplicationDetail, ...keys: string[]): boolean {
    for (const key of keys) {
      const v = raw[key];
      if (v === undefined || v === null) {
        continue;
      }
      if (typeof v === 'boolean') {
        return v;
      }
      if (typeof v === 'string') {
        return v.toLowerCase() === 'true' || v === '1';
      }
      if (typeof v === 'number') {
        return v !== 0;
      }
    }
    return false;
  }

  private static pickInt(raw: ApplicationDetail, ...keys: string[]): number | null {
    for (const key of keys) {
      const v = raw[key];
      if (v === undefined || v === null) {
        continue;
      }
      if (typeof v === 'number' && !Number.isNaN(v)) {
        return v;
      }
      const n = Number(v);
      if (!Number.isNaN(n)) {
        return n;
      }
    }
    return null;
  }

  private applyApiToForm(raw: ApplicationDetail): void {
    this.applicationId = ApplicationDetailsComponent.pickInt(raw, 'id', 'Id');
    this.readonlyClientId = ApplicationDetailsComponent.pickString(
      raw,
      'clientId',
      'ClientId'
    );
    this.readonlyAuthenticationKey = ApplicationDetailsComponent.pickString(
      raw,
      'authenticationKey',
      'AuthenticationKey'
    );
    this.formModel = {
      applicationName: ApplicationDetailsComponent.pickString(
        raw,
        'applicationName',
        'ApplicationName'
      ),
      IsActive: ApplicationDetailsComponent.pickBool(raw, 'IsActive', 'isActive'),
      ContactName: ApplicationDetailsComponent.pickString(
        raw,
        'ContactName',
        'contactName'
      ),
      ContactEmail: ApplicationDetailsComponent.pickString(
        raw,
        'ContactEmail',
        'contactEmail'
      ),
      WebhookUrl: ApplicationDetailsComponent.pickString(
        raw,
        'WebhookUrl',
        'webhookUrl'
      ),
      WebhookAuthenticationKey: ApplicationDetailsComponent.pickString(
        raw,
        'WebhookAuthenticationKey',
        'webhookAuthenticationKey'
      ),
      WebhookAuthenticationType: ApplicationDetailsComponent.normalizeAuthType(
        ApplicationDetailsComponent.pickString(
          raw,
          'WebhookAuthenticationType',
          'webhookAuthenticationType'
        )
      )
    };
  }

  private static normalizeQuestionMappings(
    raw: unknown[] | null | undefined
  ): ApplicationQuestionMapping[] {
    if (!raw?.length) {
      return [];
    }
    return raw.map((item) => {
      const mapping = item as Record<string, unknown>;
      return {
        id: ApplicationDetailsComponent.pickMappingInt(
          mapping,
          'id',
          'Id',
          'mapping_id',
          'mappingId'
        ),
        question_name: ApplicationDetailsComponent.pickMappingString(
          mapping,
          'question_name',
          'QuestionName'
        ),
        integration_question_id: ApplicationDetailsComponent.pickMappingString(
          mapping,
          'integration_question_id',
          'IntegrationQuestionId'
        ),
        question_id: ApplicationDetailsComponent.pickMappingString(
          mapping,
          'question_id',
          'QuestionId'
        )
      };
    });
  }

  private static pickMappingString(
    raw: Record<string, unknown>,
    ...keys: string[]
  ): string {
    for (const key of keys) {
      const v = raw[key];
      if (v === undefined || v === null) {
        continue;
      }
      return String(v);
    }
    return '';
  }

  private static pickMappingInt(
    raw: Record<string, unknown>,
    ...keys: string[]
  ): number | null {
    for (const key of keys) {
      const v = raw[key];
      if (v === undefined || v === null) {
        continue;
      }
      if (typeof v === 'number' && !Number.isNaN(v)) {
        return v;
      }
      const n = Number(v);
      if (!Number.isNaN(n)) {
        return n;
      }
    }
    return null;
  }

  private loadApplication(id: string): void {
    this.loading = true;
    this.applicationsService.getById(id).subscribe({
        next: (response) => {
          this.applyApiToForm(response.output);
          this.questionMappings = ApplicationDetailsComponent.normalizeQuestionMappings(
            response.questionMappings
          );
          this.loading = false;
        },
        error: () => {
          this.error = 'Failed to load application.';
          this.loading = false;
        }
      });
  }

  isMappingActionPending(mapping: ApplicationQuestionMapping): boolean {
    if (mapping.id == null) {
      return false;
    }
    return (
      this.mappingDeletingId === mapping.id ||
      (this.mappingSaving && this.editingMappingId === mapping.id)
    );
  }

  isDeletingMapping(mapping: ApplicationQuestionMapping): boolean {
    return mapping.id != null && this.mappingDeletingId === mapping.id;
  }

  openAddMappingModal(): void {
    this.openMappingModal('add');
  }

  openEditMappingModal(mapping: ApplicationQuestionMapping): void {
    this.openMappingModal('edit', mapping);
  }

  private openMappingModal(mode: 'add' | 'edit', mapping?: ApplicationQuestionMapping): void {
    this.mappingModalMode = mode;
    this.editingMappingId = mapping?.id ?? null;
    this.mappingPopoverOpen = true;
    this.mappingPopoverError = null;
    this.mappingActionError = null;
    this.resetMappingModalFields();

    this.loadConfirmatoryQuestions(() => {
      if (mode === 'edit' && mapping) {
        this.applyMappingToModal(mapping);
      }
    });
  }

  private resetMappingModalFields(): void {
    this.questionFilterText = '';
    this.selectedQuestionId = null;
    this.integrationQuestionId = '';
    this.filteredConfirmatoryQuestions = this.confirmatoryQuestions;
    this.justSendCategories = false;
    this.questionHasCategories = false;
    this.loadingQuestionCategories = false;
  }

  private applyMappingToModal(mapping: ApplicationQuestionMapping): void {
    this.integrationQuestionId = mapping.integration_question_id;
    this.questionFilterText = '';
    this.filteredConfirmatoryQuestions = this.confirmatoryQuestions.slice();
    this.ensureSelectedQuestionVisible(mapping);
    this.refreshSelectedQuestionInSelect();
    if (this.selectedQuestionId != null) {
      this.loadQuestionCategories(this.selectedQuestionId);
    }
  }

  private parseQuestionId(value: unknown): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const questionId = Number(value);
    return Number.isNaN(questionId) ? null : questionId;
  }

  private normalizeConfirmatoryQuestions(response: unknown[]): Questions[] {
    return (response as Questions[])
      .map((question) => {
        const normalized = Object.assign(new Questions(), question);
        normalized.id = this.parseQuestionId(question.id) ?? 0;
        return normalized;
      })
      .filter((question) => question.id > 0)
      .sort((a, b) => a.question_name.localeCompare(b.question_name));
  }

  private ensureSelectedQuestionVisible(mapping?: ApplicationQuestionMapping): void {
    const questionId = this.parseQuestionId(mapping?.question_id);
    if (questionId == null) {
      this.selectedQuestionId = null;
      return;
    }

    this.selectedQuestionId = questionId;

    let selected = this.confirmatoryQuestions.find(
      (question) => question.id === questionId
    );

    if (!selected && mapping) {
      selected = Object.assign(new Questions(), {
        id: questionId,
        question_name: mapping.question_name || `Question ${questionId}`
      });
      this.confirmatoryQuestions = [selected, ...this.confirmatoryQuestions];
    }

    if (!selected) {
      return;
    }

    const alreadyVisible = this.filteredConfirmatoryQuestions.some(
      (question) => question.id === questionId
    );
    if (!alreadyVisible) {
      this.filteredConfirmatoryQuestions = [selected, ...this.filteredConfirmatoryQuestions];
    }
  }

  private refreshSelectedQuestionInSelect(): void {
    const questionId = this.selectedQuestionId;
    this.selectedQuestionId = null;
    setTimeout(() => {
      this.selectedQuestionId = questionId;
    });
  }

  closeMappingPopover(): void {
    this.mappingPopoverOpen = false;
    this.mappingModalMode = 'add';
    this.editingMappingId = null;
    this.mappingPopoverError = null;
    this.resetMappingModalFields();
  }

  onSelectedQuestionChange(questionId: number | null): void {
    this.justSendCategories = false;
    if (questionId == null) {
      this.questionHasCategories = false;
      return;
    }
    this.loadQuestionCategories(questionId);
  }

  private loadQuestionCategories(questionId: number): void {
    this.loadingQuestionCategories = true;
    this.questionHasCategories = false;
    this.runService.getQuestionCats(questionId).subscribe({
      next: (categories) => {
        const list = Array.isArray(categories) ? categories : [];
        this.questionHasCategories = list.length > 0;
        this.loadingQuestionCategories = false;
      },
      error: () => {
        this.questionHasCategories = false;
        this.loadingQuestionCategories = false;
      }
    });
  }

  onQuestionFilterChange(filter: string): void {
    this.questionFilterText = filter;
    const term = filter.trim().toLowerCase();
    if (!term) {
      this.filteredConfirmatoryQuestions = this.confirmatoryQuestions.slice();
      this.refreshSelectedQuestionInSelect();
      return;
    }
    this.filteredConfirmatoryQuestions = this.confirmatoryQuestions.filter((question) =>
      question.question_name.toLowerCase().includes(term)
    );
    if (this.selectedQuestionId != null) {
      const selected = this.confirmatoryQuestions.find(
        (question) => question.id === this.selectedQuestionId
      );
      if (
        selected &&
        !this.filteredConfirmatoryQuestions.some((question) => question.id === selected.id)
      ) {
        this.filteredConfirmatoryQuestions = [selected, ...this.filteredConfirmatoryQuestions];
      }
    }
    this.refreshSelectedQuestionInSelect();
  }

  saveQuestionMapping(): void {
    if (this.mappingModalMode === 'edit') {
      this.updateQuestionMapping();
      return;
    }
    this.addQuestionMapping();
  }

  /** 1 = no categories (hidden), 2 = categories only, 3 = categories and rubric */
  private resolveCategoriesSendResponse(): number {
    if (!this.questionHasCategories) {
      return 1;
    }
    return this.justSendCategories ? 2 : 3;
  }

  addQuestionMapping(): void {
    this.mappingPopoverError = null;
    if (this.applicationId == null) {
      this.mappingPopoverError = 'Missing application id.';
      return;
    }
    if (this.selectedQuestionId == null) {
      this.mappingPopoverError = 'Select a question.';
      return;
    }
    if (!this.integrationQuestionId.trim()) {
      this.mappingPopoverError = 'Integration question ID is required.';
      return;
    }

    this.mappingSaving = true;
    this.applicationsService.addQuestionMapping({
      ApplicationId: this.applicationId,
      IntegrationQuestionId: this.integrationQuestionId.trim(),
      QuestionId: this.selectedQuestionId,
      CategoriesSendResponse: this.resolveCategoriesSendResponse()
    }).subscribe({
      next: () => {
        this.mappingSaving = false;
        this.closeMappingPopover();
        this.refreshQuestionMappings();
      },
      error: () => {
        this.mappingSaving = false;
        this.mappingPopoverError = 'Failed to add question mapping.';
      }
    });
  }

  updateQuestionMapping(): void {
    this.mappingPopoverError = null;
    if (this.editingMappingId == null) {
      this.mappingPopoverError = 'Missing mapping id.';
      return;
    }
    if (this.selectedQuestionId == null) {
      this.mappingPopoverError = 'Select a question.';
      return;
    }
    if (!this.integrationQuestionId.trim()) {
      this.mappingPopoverError = 'Integration question ID is required.';
      return;
    }

    this.mappingSaving = true;
    this.applicationsService.updateQuestionMapping({
      Id: this.editingMappingId,
      IntegrationQuestionId: this.integrationQuestionId.trim(),
      QuestionId: this.selectedQuestionId,
      CategoriesSendResponse: this.resolveCategoriesSendResponse()
    }).subscribe({
      next: () => {
        this.mappingSaving = false;
        this.closeMappingPopover();
        this.refreshQuestionMappings();
      },
      error: () => {
        this.mappingSaving = false;
        this.mappingPopoverError = 'Failed to update question mapping.';
      }
    });
  }

  deleteQuestionMapping(mapping: ApplicationQuestionMapping): void {
    this.mappingActionError = null;
    if (mapping.id == null) {
      this.mappingActionError = 'Cannot delete mapping without id.';
      return;
    }
    const label = mapping.question_name || mapping.integration_question_id || 'this mapping';
    if (!window.confirm(`Delete mapping for "${label}"?`)) {
      return;
    }

    this.mappingDeletingId = mapping.id;
    this.applicationsService.deleteQuestionMapping(mapping.id).subscribe({
      next: () => {
        this.mappingDeletingId = null;
        this.refreshQuestionMappings();
      },
      error: () => {
        this.mappingDeletingId = null;
        this.mappingActionError = 'Failed to delete question mapping.';
      }
    });
  }

  private loadConfirmatoryQuestions(onLoaded?: () => void): void {
    if (this.confirmatoryQuestionsLoaded) {
      onLoaded?.();
      return;
    }
    this.loadingQuestions = true;
    this.runService.getQuestions().subscribe({
      next: (response) => {
        const sorted = this.normalizeConfirmatoryQuestions(response as unknown[]);
        this.confirmatoryQuestions = sorted;
        this.filteredConfirmatoryQuestions = sorted.slice();
        this.confirmatoryQuestionsLoaded = true;
        this.loadingQuestions = false;
        onLoaded?.();
      },
      error: () => {
        this.mappingPopoverError = 'Failed to load questions.';
        this.loadingQuestions = false;
      }
    });
  }

  private refreshQuestionMappings(): void {
    if (!this.id) {
      return;
    }
    this.applicationsService.getById(this.id).subscribe({
      next: (response) => {
        this.questionMappings = ApplicationDetailsComponent.normalizeQuestionMappings(
          response.questionMappings
        );
      }
    });
  }

  save(): void {
    this.saveError = null;
    this.saveSuccess = null;
    if (this.isCreateMode) {
      this.createApplication();
      return;
    }
    if (this.applicationId == null) {
      this.saveError = 'Missing application id.';
      return;
    }
    this.updateApplication();
  }

  toggleActive(): void {
    this.saveError = null;
    this.saveSuccess = null;
    if (this.applicationId == null) {
      this.saveError = 'Missing application id.';
      return;
    }

    const nextIsActive = !this.formModel.IsActive;
    const body = {
      id: this.applicationId,
      isActive: nextIsActive
    };

    this.statusSaving = true;
    this.applicationsService.setStatus(body.id, body.isActive).subscribe({
      next: () => {
        this.statusSaving = false;
        this.formModel.IsActive = nextIsActive;
        this.saveSuccess = nextIsActive ? 'Application is now Active.' : 'Application is now Inactive.';
      },
      error: () => {
        this.statusSaving = false;
        this.saveError = 'Status update failed.';
      }
    });
  }

  private createApplication(): void {
    const body: ApplicationCreateRequestBody = {
      isActive: this.formModel.IsActive,
      applicationName: this.formModel.applicationName,
      contactName: this.formModel.ContactName,
      contactEmail: this.formModel.ContactEmail,
      webhookUrl: this.formModel.WebhookUrl,
      webhookAuthenticationKey: this.formModel.WebhookAuthenticationKey,
      webhookAuthenticationType: this.formModel.WebhookAuthenticationType
    };
    this.saving = true;
    this.applicationsService.create(body).subscribe({
      next: (newId) => {
        this.saving = false;
        const id = typeof newId === 'number' ? newId : Number(newId);
        if (!Number.isNaN(id)) {
          this.router.navigate(['/application-details', id]);
        }
      },
      error: () => {
        this.saveError = 'Create failed.';
        this.saving = false;
      }
    });
  }

  private updateApplication(): void {
    const body: ApplicationUpdateRequestBody = {
      id: this.applicationId as number,
      applicationName: this.formModel.applicationName,
      contactName: this.formModel.ContactName,
      contactEmail: this.formModel.ContactEmail,
      webhookUrl: this.formModel.WebhookUrl,
      webhookAuthenticationKey: this.formModel.WebhookAuthenticationKey,
      webhookAuthenticationType: this.formModel.WebhookAuthenticationType
    };
    this.saving = true;
    this.applicationsService.update(body).subscribe({
      next: () => {
        this.saving = false;
        this.saveSuccess = 'Successfully updated.';
      },
      error: () => {
        this.saveError = 'Update failed.';
        this.saving = false;
      }
    });
  }
}
